import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
export function ReportDetail() {
  const [searchParams] = useSearchParams();
  const reportType = searchParams.get('report') || 'deep-self-decode';
  // Report-specific content keyed by report type
  const reportContent: Record<string, any> = {
    'deep-self-decode': {
      name: 'Deep Self Decode',
      subtitle: 'A full diagnostic of your internal pattern architecture',
      oneLiner: 'This report maps why your core patterns exist, how they operate across life contexts, and what keeps them in place.',
      estimatedTime: '5–7 minutes',
      metaLine: 'Estimated time: 5–7 minutes · One-time diagnostic',
      price: '₹499',
      originalPrice: '₹1,499',
      priceLabel: 'Launch price · One-time diagnostic',
      diagnoses: ['How your core patterns form and run automatically', 'Where the same pattern repeats across different life contexts', 'What constraints keep the pattern active', 'Where the pattern can be interrupted or recalibrated'],
      diagnosticLayers: ['Pattern mechanics — how the pattern triggers, loops, and reinforces itself', 'Pattern origin — inferred conditions under which the pattern likely formed', 'Constraint map — what keeps the pattern active today', 'Cross-context view — how the same pattern appears in work, relationships, and decisions'],
      doesNotDo: ['Does not give advice or action plans', 'Does not predict future behavior', 'Does not assign personality labels', 'Does not replace therapy or coaching'],
      inputsRequired: {
        items: ['Name, date of birth, time, and place of birth', 'Short diagnostic questions about pattern context and triggers'],
        totalTime: '~5–7 minutes'
      }
    },
    'work-self-decode': {
      name: 'Work Self Decode',
      subtitle: 'A diagnostic of your work pattern architecture',
      oneLiner: 'This report maps how your core patterns activate under work pressure and what keeps them in place professionally.',
      estimatedTime: '5–7 minutes',
      metaLine: 'Estimated time: 5–7 minutes · One-time diagnostic',
      price: '₹999',
      diagnoses: ['How internal patterns activate under work pressure', 'Response patterns in authority, performance, and visibility contexts', 'What work situations trigger automatic defensive responses', 'Where work constraints are self-imposed versus external'],
      diagnosticLayers: ['Work pattern mechanics — how patterns operate in professional contexts', 'Trigger map — specific work situations that activate patterns', 'Response architecture — what you do automatically when patterns activate', 'Constraint analysis — what limits professional range and where leverage exists'],
      doesNotDo: ['Does not evaluate job fit or career direction', 'Does not assess competence or performance', 'Does not provide career advice', 'Does not replace professional coaching'],
      inputsRequired: {
        items: ['Name, date of birth, time, and place of birth', 'Diagnostic questions about work context, pressure points, and response patterns'],
        totalTime: '~5–7 minutes'
      }
    },
    'relationship-decode': {
      name: 'Relationship Decode',
      subtitle: 'A diagnostic of relational pattern architecture',
      oneLiner: 'This report maps the interaction system between two people — how patterns activate, reinforce, and create repeated friction.',
      estimatedTime: '6–8 minutes',
      metaLine: 'Estimated time: 6–8 minutes · One-time diagnostic',
      price: '₹499',
      originalPrice: '₹1,999',
      priceLabel: 'Launch price · One-time diagnostic',
      diagnosesText: "This report diagnoses the interaction system between two people. It explains: how relational patterns activate under pressure, how each person's responses stabilize the dynamic, why the same friction keeps repeating even when intent is good, and how clarity collapses once the interaction starts. The focus is not on individuals, blame, or behavior correction — but on the system created when two response patterns interact.",
      diagnosticLayersText: "This report shows a system-level map of the relationship dynamic, the primary interaction pattern driving repeated friction, a secondary dynamic that emerges under sustained strain, how each person's responses unintentionally reinforce the loop, the emotional, relational, and decision costs of the pattern over time, and conditions where the dynamic loosens and clarity temporarily returns. This is not a surface explanation of conflict. It is a diagnosis of why the interaction reliably ends up here.",
      doesNotDo: ['Does not judge either person', 'Does not determine who is right or wrong', 'Does not predict relationship outcomes or success', 'Does not provide relationship advice or action plans', 'Does not replace couples therapy or counseling'],
      doesNotDoClosing: 'It explains the system — it does not attempt to fix it.',
      inputsRequired: {
        items: ['Baseline details for both individuals (used as structural reference, not prediction)', 'A short set of diagnostic questions about: repeating friction, response patterns, outcome loops, duration and pressure points. No narrative writing. No emotional venting. No right or wrong answers.'],
        totalTime: '~6–8 minutes'
      },
      whenUseful: {
        intro: 'This report is especially useful when:',
        points: ['the same conflict keeps resurfacing', 'conversations derail despite good intent', 'closeness alternates with distance', 'clarity feels intact until interaction begins'],
        closing: "If you're looking for reassurance or advice, this is not the right tool. If you want to understand why this dynamic keeps repeating, this is."
      }
    }
  };
  const reportData = reportContent[reportType] || reportContent['deep-self-decode'];
  const handleRunDiagnostic = () => {
    localStorage.setItem('selectedReport', reportType);
  };
  return <>
      <Helmet data-id="element-3607">
        <title data-id="element-3608">{reportData.name} — Decode | Lazy Monk Labs</title>
        <meta name="description" content={`Full diagnostic report: ${reportData.name}`} data-id="element-3609" />
        <meta name="robots" content="noindex, nofollow" data-id="element-3610" />
        <link rel="canonical" href="https://lazymonklabs.com/report-detail" data-id="element-3611" />
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-3612"></script>
        <script data-id="element-3613">{`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-QCXDHW3F0P');`}</script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-3614">
        <header data-id="element-3615">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-3616">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3617">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-3618">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3619" />
                <span className="text-lg font-medium tracking-tight" data-id="element-3620">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-3621">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-3622">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-3623">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-24 px-6" data-id="element-3624">
          <div className="max-w-2xl mx-auto" data-id="element-3625">
            {/* HERO SECTION */}
            <div className="mb-20" data-id="element-3626">
              <h1 className="text-4xl md:text-5xl font-medium mb-4 leading-tight tracking-tight" data-id="element-3627">
                {reportData.name}
              </h1>
              <p className="text-lg text-[#F5F5F5]/60 italic mb-6 leading-relaxed" data-id="element-3628">
                {reportData.subtitle}
              </p>
              <p className="text-base text-[#F5F5F5]/70 mb-6 leading-relaxed max-w-xl" data-id="element-3629">
                {reportData.oneLiner}
              </p>
              <p className="text-xs text-[#F5F5F5]/40" data-id="element-3630">{reportData.metaLine}</p>
            </div>

            {/* SECTION: What this report diagnoses */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]/50" data-id="element-3631">
              <h2 className="text-xl font-medium mb-6 tracking-tight" data-id="element-3632">
                What this report diagnoses
              </h2>
              {reportData.diagnosesText ? <p className="text-[#F5F5F5]/70 leading-relaxed max-w-xl" data-id="element-3633">
                  {reportData.diagnosesText}
                </p> : <ul className="space-y-3" data-id="element-3634">
                  {reportData.diagnoses.map((item: string, index: number) => <li key={index} className="flex gap-3" data-id="element-3635">
                      <span className="text-[#F5F5F5]/30 mt-1 text-sm" data-id="element-3636">•</span>
                      <span className="text-[#F5F5F5]/70 leading-relaxed flex-1 max-w-xl" data-id="element-3637">
                        {item}
                      </span>
                    </li>)}
                </ul>}
            </section>

            {/* SECTION: Diagnostic layers you'll see */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]/50" data-id="element-3638">
              <h2 className="text-xl font-medium mb-6 tracking-tight" data-id="element-3639">
                Diagnostic layers you'll see
              </h2>
              {reportData.diagnosticLayersText ? <p className="text-[#F5F5F5]/70 leading-relaxed max-w-xl" data-id="element-3640">
                  {reportData.diagnosticLayersText}
                </p> : <ul className="space-y-3" data-id="element-3641">
                  {reportData.diagnosticLayers.map((item: string, index: number) => <li key={index} className="flex gap-3" data-id="element-3642">
                        <span className="text-[#F5F5F5]/30 mt-1 text-sm" data-id="element-3643">
                          •
                        </span>
                        <span className="text-[#F5F5F5]/70 leading-relaxed flex-1 max-w-xl" data-id="element-3644">
                          {item}
                        </span>
                      </li>)}
                </ul>}
            </section>

            {/* SECTION: What this report does not do */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]/50" data-id="element-3645">
              <h2 className="text-xl font-medium mb-6 tracking-tight" data-id="element-3646">
                What this report does not do
              </h2>
              <ul className="space-y-3 mb-4" data-id="element-3647">
                {reportData.doesNotDo.map((item: string, index: number) => <li key={index} className="flex gap-3" data-id="element-3648">
                    <span className="text-[#F5F5F5]/30 mt-1 text-sm" data-id="element-3649">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1 max-w-xl" data-id="element-3650">
                      {item}
                    </span>
                  </li>)}
              </ul>
              {reportData.doesNotDoClosing && <p className="text-[#F5F5F5]/70 leading-relaxed max-w-xl" data-id="element-3651">
                  {reportData.doesNotDoClosing}
                </p>}
            </section>

            {/* SECTION: Inputs required */}
            <section className="mb-16 pb-16 border-b border-[#2B2B2B]/50" data-id="element-3652">
              <h2 className="text-xl font-medium mb-6 tracking-tight" data-id="element-3653">
                Inputs required
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed mb-4 max-w-xl" data-id="element-3654">
                To generate an accurate diagnostic, you'll provide:
              </p>
              <ul className="space-y-3 mb-4" data-id="element-3655">
                {reportData.inputsRequired.items.map((item: string, index: number) => <li key={index} className="flex gap-3" data-id="element-3656">
                      <span className="text-[#F5F5F5]/30 mt-1 text-sm" data-id="element-3657">•</span>
                      <span className="text-[#F5F5F5]/70 leading-relaxed flex-1 max-w-xl" data-id="element-3658">
                        {item}
                      </span>
                    </li>)}
              </ul>
              <p className="text-xs text-[#F5F5F5]/50" data-id="element-3659">
                Total input time: {reportData.inputsRequired.totalTime}
              </p>
            </section>

            {/* Optional Section: When this report is most useful */}
            {reportData.whenUseful && <section className="mb-16 pb-16 border-b border-[#2B2B2B]/50" data-id="element-3660">
                <h2 className="text-xl font-medium mb-6 tracking-tight" data-id="element-3661">
                  When this report is most useful
                </h2>
                <p className="text-[#F5F5F5]/70 leading-relaxed mb-4 max-w-xl" data-id="element-3662">
                  {reportData.whenUseful.intro}
                </p>
                <ul className="space-y-3 mb-6" data-id="element-3663">
                  {reportData.whenUseful.points.map((point: string, index: number) => <li key={index} className="flex gap-3" data-id="element-3664">
                        <span className="text-[#F5F5F5]/30 mt-1 text-sm" data-id="element-3665">
                          •
                        </span>
                        <span className="text-[#F5F5F5]/70 leading-relaxed flex-1 max-w-xl" data-id="element-3666">
                          {point}
                        </span>
                      </li>)}
                </ul>
                <p className="text-[#F5F5F5]/70 leading-relaxed max-w-xl" data-id="element-3667">
                  {reportData.whenUseful.closing}
                </p>
              </section>}

            {/* PRICING & CTA (LOW EMPHASIS) */}
            <section className="mb-16" data-id="element-3668">
              <div className="mb-8" data-id="element-3669">
                <div className="flex items-baseline gap-3 mb-2" data-id="element-3670">
                  {reportData.originalPrice && <span className="text-lg text-[#F5F5F5]/20 line-through" data-id="element-3671">
                      {reportData.originalPrice}
                    </span>}
                  <span className="text-3xl font-medium" data-id="element-3672">
                    {reportData.price}
                  </span>
                </div>
                <p className="text-xs text-[#F5F5F5]/40" data-id="element-3673">
                  {reportData.priceLabel}
                </p>
              </div>

              {/* PRIMARY CTA */}
              <Link to="/login" onClick={handleRunDiagnostic} className="block w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors" data-id="element-3674">
                Run {reportData.name}
              </Link>
              <p className="text-xs text-[#F5F5F5]/40 text-center mt-3" data-id="element-3675">
                A full diagnostic of this system
              </p>
            </section>
          </div>
        </main>

        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-3676">
          <div className="max-w-7xl mx-auto text-center" data-id="element-3677">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-3678">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}